import React from 'react';
import { handleKeyEvent } from 'Utils';
import { Icon } from '@brighterion/br-ui';
import { ColumnInstance } from 'react-table';
import clsx from 'clsx';
import styles from '../THead.css';

const getSortIconName = (isSorted: boolean, isSortedDesc: boolean | undefined) => {
    if (!isSorted) return 'sort-arrow-none';
    if (isSortedDesc) return 'sort-arrow-down';
    return 'sort-arrow-up';
};

const getAriaSort = (isSorted: boolean, isSortedDesc: boolean | undefined) => {
    if (!isSorted) return 'none';
    if (isSortedDesc) return 'descending';
    return 'ascending';
};

const getHeaderClassName = (disabledSort: boolean, isSorted: boolean) => {
    let headerClassName = [styles.colHeader, disabledSort && styles.inactive];
    if (isSorted) {
        headerClassName = [...headerClassName, styles.isSorted];
    }
    return headerClassName;
};

export default function HeaderCell<D extends object = {}>({
    column,
    isExecutionOrderSelectOption
}: {
    column: ColumnInstance<D>;
    isExecutionOrderSelectOption?: boolean;
}) {
    const disableSortBy = Boolean(column?.disableSortBy);
    const sortEnabled = !disableSortBy;
    const toggleProps = column?.getSortByToggleProps?.();
    const headerProps = column.getHeaderProps([
        {
            ...toggleProps,
            ...{
                role: 'columnheader',
                'aria-label': typeof column.render('Header') === 'string' ? column.render('Header') : '',
                className: clsx('th', getHeaderClassName(disableSortBy, column?.isSorted)),
                // we only need these properties when there is a sort
                // no sense in having aria-sort/tabIndex if can't even sort
                ...(sortEnabled && {
                    tabIndex: 0,
                    onKeyDown: handleKeyEvent((toggleProps as any)?.onClick),
                    'aria-sort': getAriaSort(column.isSorted, column.isSortedDesc)
                })
            },
            style: {
                maxWidth: `${column.maxWidth}px`,
                ...column?.style
            }
        }
    ]);

    if (column.isGrouped) {
        return null;
    }
    const sortIconName = getSortIconName(column.isSorted, column.isSortedDesc);

    return (
        <div {...headerProps} data-testid={`${column.id}-sort-indicator`}>
            <div className={styles.innerWrapper}>
                {column.render('Header')}
                {column.canSort && (
                    <span className={styles.sortIndicator}>
                        <Icon name={sortIconName} size='md' />
                    </span>
                )}
                {isExecutionOrderSelectOption && (column.id === 'priority' || column.id === 'id') ? (
                    <span className={clsx(styles.greyArrow, styles.sortIndicator)}>
                        <Icon name='caret-up' size='md' />
                    </span>
                ) : null}
            </div>
        </div>
    );
}
